import { useEffect, useRef } from 'react'

/**
 * Custom hook that ensures the effect runs only once, even in React StrictMode.
 * This prevents double API calls in development mode.
 * 
 * @param {Function} effect - The effect function to run
 * @param {Array} dependencies - Optional dependency array (defaults to empty array)
 */
export function useEffectOnce(effect, dependencies = []) {
  const hasRun = useRef(false)
  const cleanupRef = useRef(null)

  useEffect(() => {
    // In production, or if already run, skip
    if (hasRun.current) {
      return cleanupRef.current
    }

    hasRun.current = true
    cleanupRef.current = effect()

    return () => {
      if (cleanupRef.current) {
        cleanupRef.current()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies)
}

/**
 * Custom hook for async data fetching that prevents double requests in StrictMode.
 * Returns a cleanup function automatically.
 * 
 * @param {Function} asyncFunction - Async function to execute
 * @param {Array} dependencies - Dependency array
 * @returns {void}
 * 
 * @example
 * useAsyncEffect(async (cancelled) => {
 *   const data = await fetchData()
 *   if (cancelled()) return
 *   setData(data)
 * }, [])
 */
export function useAsyncEffect(asyncFunction, dependencies = []) {
  useEffect(() => {
    let cancelled = false
    const getCancelled = () => cancelled

    asyncFunction(getCancelled)

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies)
}
